import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";

import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { View } from "react-native";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WidgetKPI } from "widgets/src/WidgetKPI";
import { WAIntegrationKPIQuery } from "./__generated__/WAIntegrationKPIQuery.graphql";
import { WAIntegrationKPIMutation } from "./__generated__/WAIntegrationKPIMutation.graphql";

type Props = UserLoggedInNavigatorScreenProps<"WidgetAddIntegrationKPI">;

export const WAIntegrationKPI = withWebModal(WAIntegrationKPIC, true);
function WAIntegrationKPIC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAIntegrationKPI {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAIntegrationKPI(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WAIntegrationKPIQuery>(
    graphql`
      query WAIntegrationKPIQuery(
        $storecastId: ID!
        $input: PreviewWidgetKPIInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetKPI(input: $input) {
            dataSource
            ...WidgetKPI_widgetKPI
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
      input: {
        size: "TWO_X_FOUR",
        dataSource: props.route.params.dataSource,
      },
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [createWidget, loading] = useMutation<WAIntegrationKPIMutation>(graphql`
    mutation WAIntegrationKPIMutation(
      $storecastId: ID!
      $input: CreateWidgetKPIInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        createWidgetKPI(widget: $input) {
          id
        }
      }
    }
  `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidget({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
            dataSource: props.route.params.dataSource,
          },
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidget,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
      props.route.params.dataSource,
    ]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  return (
    <View style={{ flex: 1 }}>
      {isCompact ? null : <WAFakeHeader />}
      <WASizePreview
        fullScreen
        loading={loading}
        maxSize={props.route.params.maxSize}
        onCreate={onCreate}
        unitSize={props.route.params.unitSize}
        widgetSizes={overviewWidgetSizes}
        defaultSize={{ w: 2, h: 2 }}
      >
        {data.dashboard.previewWidgetKPI ? (
          <WidgetKPI
            widget={data.dashboard.previewWidgetKPI}
            storecastId={props.route.params.storecastId}
            interactive={false}
            mode="IDLE"
          />
        ) : null}
      </WASizePreview>
    </View>
  );
}
